import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { mockSongs } from '../data/mock';
import { X } from 'lucide-react';
import audioSyncHandler from '../services/AudioSyncHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = () => {
  try {
    const url = new URL(API_URL);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}`;
  } catch (e) {
    console.error('[SessionContext] Invalid API_URL for WebSocket:', API_URL);
    return null;
  }
};

const SessionContext = createContext(null);

// Session Ended Modal Component
const SessionEndedModal = ({ message, onDismiss }) => {
  const navigate = useNavigate();
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 max-w-sm w-full text-center relative overflow-hidden animate-scale-in">
        {/* Icon - Blue color */}
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Session Ended</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">{message}</p>
        
        <button
          onClick={() => {
            onDismiss();
            navigate('/app');
          }}
          className="w-full py-3.5 rounded-2xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
          data-testid="session-ended-ok"
        >
          OK
        </button>
      </div>
    </div>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const { api, user } = useAuth();
  const [currentSession, setCurrentSession] = useState(null);
  const [mySessions, setMySessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInActiveSession, setIsInActiveSession] = useState(false);
  
  // Persistent session state - initialize from localStorage if available
  const [activeSessionData, setActiveSessionData] = useState(() => {
    try {
      const stored = localStorage.getItem('tapInActiveSession');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showLeaveSessionModal, setShowLeaveSessionModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Session ended by host modal
  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);
  const [sessionEndedMessage, setSessionEndedMessage] = useState('');
  
  // Track if current user is ending the session (to skip showing them the modal)
  const isEndingSessionRef = useRef(false);

  // Callback to stop MusicKit when session ends
  const stopMusicCallbackRef = useRef(null);

  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [sessionUsers, setSessionUsers] = useState([]);
  const [fireCount, setFireCount] = useState(0);
  // Per-song fire counts: { songId: count }
  const [songFireCounts, setSongFireCounts] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const wsConnectedTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false); // Prevent duplicate connection attempts
  const currentSessionIdRef = useRef(null); // Track current session ID
  
  // Audio sync callback ref - allows SessionView to register for AUDIO_SYNC messages
  const audioSyncCallbackRef = useRef(null);
  
  // Queue for AUDIO_SYNC messages that arrive before callback is registered
  const pendingAudioSyncMessagesRef = useRef([]);
  
  // Track whether INITIAL_SYNC has been received for the current connection
  // Prevents sync_state from overwriting more accurate INITIAL_SYNC playback state
  const hasReceivedInitialSyncRef = useRef(false);
  
  // Profile image cache - persists across WebSocket reconnections
  const profileImageCacheRef = useRef({});

  // Song queue for the active session - now synced via WebSocket
  const [songQueue, setSongQueue] = useState([]);
  const songQueueRef = useRef(songQueue);
  const currentTrackIndexRef = useRef(currentTrackIndex);
  
  // Keep refs updated for use in WebSocket message handler (avoids stale closures)
  useEffect(() => { songQueueRef.current = songQueue; }, [songQueue]);
  useEffect(() => { currentTrackIndexRef.current = currentTrackIndex; }, [currentTrackIndex]);
  
  // Track which songs the user has fired (to prevent multiple fires per song)
  const [firedSongIds, setFiredSongIds] = useState(new Set());
  
  // Save active session to localStorage when it changes
  // Note: isInActiveSession is ONLY true when WebSocket is connected
  // IMPORTANT: Only store essential fields to avoid QuotaExceededError
  useEffect(() => {
    if (activeSessionData) {
      try {
        // Only store essential session data - exclude large fields like cover_image
        const essentialData = {
          id: activeSessionData.id,
          title: activeSessionData.title,
          host_id: activeSessionData.host_id,
          host: activeSessionData.host ? {
            id: activeSessionData.host.id,
            username: activeSessionData.host.username,
            display_name: activeSessionData.host.display_name
          } : null,
          is_public: activeSessionData.is_public,
          max_guests: activeSessionData.max_guests,
          created_at: activeSessionData.created_at
          // Deliberately exclude: cover_image, guests (large arrays), co_hosts
        };
        localStorage.setItem('tapInActiveSession', JSON.stringify(essentialData));
      } catch (e) {
        // If localStorage fails (quota exceeded), just continue without persistence
        console.warn('Could not save session to localStorage:', e.message);
        localStorage.removeItem('tapInActiveSession');
      }
    } else {
      localStorage.removeItem('tapInActiveSession');
      setIsInActiveSession(false);
    }
  }, [activeSessionData]);
  
  // isInActiveSession depends on both activeSessionData AND wsConnected
  useEffect(() => {
    setIsInActiveSession(!!activeSessionData && wsConnected);
  }, [activeSessionData, wsConnected]);

  // Fetch missing profile images for session users and update state
  const enrichUsersWithProfileImages = useCallback(async (users) => {
    if (!users || users.length === 0) return;
    const token = localStorage.getItem('tapInToken');
    if (!token) return;

    // Find users needing enrichment (missing profile images or any user for username accuracy)
    const allIds = users.filter(u => u.id).map(u => u.id);
    const missingImageIds = users
      .filter(u => !u.profileImage && !u.profile_image && u.id && !profileImageCacheRef.current[u.id])
      .map(u => u.id);

    if (missingImageIds.length === 0) {
      // Apply cached images to users missing them
      const enriched = users.map(u => {
        if (!u.profileImage && !u.profile_image && profileImageCacheRef.current[u.id]) {
          return { ...u, profileImage: profileImageCacheRef.current[u.id] };
        }
        return u;
      });
      const changed = enriched.some((u, i) => u.profileImage !== users[i].profileImage);
      if (changed) setSessionUsers(enriched);
      // Still fetch from API to ensure usernames are accurate
      if (allIds.length === 0) return;
    }

    const idsToFetch = missingImageIds.length > 0 ? allIds : allIds;

    try {
      const res = await fetch(`${API_URL}/api/users/batch-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_ids: idsToFetch }),
      });
      if (!res.ok) return;
      const profiles = await res.json();

      // Cache the fetched profile images
      profiles.forEach(p => {
        if (p.profile_image) {
          profileImageCacheRef.current[p.id] = p.profile_image;
        }
      });

      // Build a lookup for username updates
      const profileMap = {};
      profiles.forEach(p => { profileMap[p.id] = p; });

      // Merge profile images AND usernames into session users
      setSessionUsers(prev => prev.map(u => {
        const profile = profileMap[u.id];
        const updates = {};
        if (!u.profileImage && !u.profile_image && profileImageCacheRef.current[u.id]) {
          updates.profileImage = profileImageCacheRef.current[u.id];
        }
        if (profile?.username && profile.username !== u.username) {
          updates.username = profile.username;
        }
        return Object.keys(updates).length > 0 ? { ...u, ...updates } : u;
      }));
    } catch (err) {
      console.warn('[SessionContext] Failed to fetch profile images:', err.message);
    }
  }, []);
  
  // Connect to WebSocket for a session
  const connectWebSocket = useCallback((sessionId) => {
    if (!user) return;
    
    // Prevent duplicate connection attempts
    if (isConnectingRef.current) {
      console.log('[SessionContext] Already connecting, skipping duplicate attempt');
      return;
    }
    
    // Don't reconnect if already connected to this session
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && currentSessionIdRef.current === sessionId) {
      console.log('[SessionContext] Already connected to this session, skipping');
      return;
    }
    
    // Close existing connection properly
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('[SessionContext] Closing existing connection before reconnecting');
      wsRef.current.close(1000, 'Switching sessions');
      wsRef.current = null;
    }
    
    isConnectingRef.current = true;
    currentSessionIdRef.current = sessionId;

    const wsBase = getWebSocketUrl();
    if (!wsBase) {
      console.error('[SessionContext] Cannot connect WebSocket - invalid URL');
      isConnectingRef.current = false;
      return;
    }

    // Only include profile image URL if it's a URL (not a base64 data string)
    let profileImageParam = '';
    if (user.profile_image && !user.profile_image.startsWith('data:')) {
      profileImageParam = encodeURIComponent(user.profile_image);
    }
    
    let ws;
    try {
      const wsUrl = `${wsBase}/api/ws/session/${sessionId}?user_id=${user.id}&username=${encodeURIComponent(user.username || user.display_name || 'Guest')}&profile_image=${profileImageParam}`;
      console.log('[SessionContext] Connecting to WebSocket:', sessionId);
      ws = new WebSocket(wsUrl);
    } catch (e) {
      console.error('[SessionContext] Failed to create WebSocket:', e);
      isConnectingRef.current = false;
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[SessionContext] WebSocket connected to session:', sessionId);
      isConnectingRef.current = false;
      // Clear any pending disconnect timeout
      if (wsConnectedTimeoutRef.current) {
        clearTimeout(wsConnectedTimeoutRef.current);
      }
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        // Reduce logging spam - only log non-routine messages
        if (message.type !== 'sync_state' && message.type !== 'AUDIO_SYNC' || process.env.NODE_ENV === 'development') {
          console.log('[SessionContext] WS message:', message.type);
        }

        switch (message.type) {
          // ====== AUDIO SYNC MESSAGES (CRITICAL FOR REAL-TIME SYNC) ======
          case 'AUDIO_SYNC':
            // Handle real-time audio synchronization
            // CRITICAL: Update UI state from AUDIO_SYNC messages (single source of truth)
            console.log('[SessionContext] AUDIO_SYNC received:', message.action);
            
            if (message.action === 'PLAY') {
              setIsPlaying(true);
              if (message.start_position !== undefined) {
                setCurrentTime(message.start_position / 1000); // ms to seconds
              }
              // Use authoritative track_index from server when available
              if (message.track_index !== undefined) {
                setCurrentTrackIndex(message.track_index);
              }
            } else if (message.action === 'PAUSE') {
              setIsPlaying(false);
              if (message.paused_at !== undefined) {
                setCurrentTime(message.paused_at / 1000);
              }
            } else if (message.action === 'SEEK') {
              if (message.position !== undefined) {
                setCurrentTime(message.position / 1000);
              }
            } else if (message.action === 'TRACK_CHANGE') {
              // Use authoritative track_index from server when available
              if (message.track_index !== undefined) {
                setCurrentTrackIndex(message.track_index);
              } else if (message.track_id) {
                // Fallback: find track index by matching track_id in the queue
                const queue = songQueueRef.current || [];
                const idx = queue.findIndex(t => t.id === message.track_id);
                if (idx >= 0) {
                  setCurrentTrackIndex(idx);
                }
              }
              setCurrentTime(0);
            } else if (message.action === 'SYNC_UPDATE') {
              // Periodic sync — keep UI state aligned
              if (message.is_playing !== undefined) {
                setIsPlaying(message.is_playing);
              }
              if (message.current_position !== undefined) {
                setCurrentTime(message.current_position / 1000);
              }
              // Use authoritative track_index from server when available
              if (message.track_index !== undefined) {
                if (message.track_index !== currentTrackIndexRef.current) {
                  console.log('[SessionContext] SYNC_UPDATE correcting track index to:', message.track_index);
                  setCurrentTrackIndex(message.track_index);
                }
              } else if (message.track_id) {
                // Fallback: self-correct track by ID if guest drifted
                const queue = songQueueRef.current || [];
                const idx = queue.findIndex(t => t.id === message.track_id);
                if (idx >= 0 && idx !== currentTrackIndexRef.current) {
                  console.log('[SessionContext] SYNC_UPDATE correcting track index (by ID) to:', idx);
                  setCurrentTrackIndex(idx);
                }
              }
            } else if (message.action === 'INITIAL_SYNC') {
              // Mark initial sync received, but ONLY block sync_state if we got real data
              // If track_id is null (blank session from server restart), let sync_state correct it
              hasReceivedInitialSyncRef.current = !!message.track_id;
              if (message.is_playing !== undefined) {
                setIsPlaying(message.is_playing);
              }
              if (message.current_position !== undefined) {
                setCurrentTime(message.current_position / 1000);
              }
              // Use authoritative track_index from server when available
              if (message.track_index !== undefined) {
                setCurrentTrackIndex(message.track_index);
              }
            }
            
            // Forward to AudioSyncHandler for MusicKit playback control
            if (audioSyncCallbackRef.current) {
              audioSyncCallbackRef.current(message);
            } else {
              // Callback not ready yet - queue the message (especially important for INITIAL_SYNC)
              console.log('[SessionContext] Callback not ready, queueing AUDIO_SYNC message:', message.action);
              pendingAudioSyncMessagesRef.current.push(message);
            }
            break;
            
          case 'sync_state':
            // Full state sync on connect
            if (message.state) {
              setSongQueue(message.state.queue || []);
              
              // Only set playback state from sync_state if INITIAL_SYNC hasn't arrived yet.
              // INITIAL_SYNC from audio_sync_manager is authoritative for position/playing/track state.
              // sync_state from websocket_manager may have stale DB values that would overwrite it.
              if (!hasReceivedInitialSyncRef.current) {
                setIsPlaying(message.state.is_playing || false);
                setCurrentTrackIndex(message.state.current_track_index || 0);
                setCurrentTime(message.state.current_time || 0);
              }
              
              // CRITICAL: Preload profile images for instant display
              const users = message.state.users || [];
              users.forEach(user => {
                if (user.profileImage) {
                  const img = new Image();
                  img.src = user.profileImage;
                }
              });
              setSessionUsers(users);
              // Fetch missing profile images from API (base64 images stripped by WebSocket)
              enrichUsersWithProfileImages(users);
              setFireCount(message.state.fire_count || 0);
              
              // Restore per-song fire counts
              if (message.song_fire_counts) {
                setSongFireCounts(message.song_fire_counts);
              }
              
              // Restore user's fired songs from server (permanent tracking)
              if (message.user_fired_songs && message.user_fired_songs.length > 0) {
                setFiredSongIds(new Set(message.user_fired_songs));
              }
              
              // Update session data with settings from WebSocket state
              setActiveSessionData(prev => {
                if (!prev) return prev;
                const updates = {};
                
                // CRITICAL: Update session title if provided
                if (message.state.title && message.state.title !== prev.title) {
                  updates.title = message.state.title;
                  console.log('[SessionContext] Syncing session title:', message.state.title);
                }
                
                // Update cover_image if provided
                if (message.state.cover_image && message.state.cover_image !== prev.cover_image) {
                  updates.cover_image = message.state.cover_image;
                }
                
                // CRITICAL: host_id should NEVER change after session creation
                // Only set it if prev.host_id is not set (first sync)
                if (message.state.host_id && !prev.host_id) {
                  updates.host_id = message.state.host_id;
                  console.log('[SessionContext] Setting initial host_id:', message.state.host_id);
                }
                
                // Update guest permission settings
                if (message.state.song_limit_per_guest !== undefined) {
                  updates.song_limit_per_guest = message.state.song_limit_per_guest;
                }
                if (message.state.guests_can_add_songs !== undefined) {
                  updates.guests_can_add_songs = message.state.guests_can_add_songs;
                }
                if (message.state.guests_can_invite !== undefined) {
                  updates.guests_can_invite = message.state.guests_can_invite;
                }
                
                // Only update if there are changes
                if (Object.keys(updates).length === 0) {
                  return prev;
                }
                
                return { ...prev, ...updates };
              });
            }
            break;
          
          case 'reaction_rejected':
            // Fire was rejected (already fired on this song)
            console.log('[SessionContext] Reaction rejected:', message.reason);
            break;
          
          case 'session_update':
            // Handle session updates (like cover image changes)
            if (message.cover_image) {
              setActiveSessionData(prev => prev ? { ...prev, cover_image: message.cover_image } : prev);
            }
            break;
          
          case 'playback_update':
            console.log('[SessionContext] Playback update - is_playing:', message.is_playing);
            setIsPlaying(message.is_playing);
            if (message.current_time !== undefined && message.current_time !== null) {
              setCurrentTime(message.current_time);
            }
            break;
          
          case 'track_change':
            setCurrentTrackIndex(message.track_index);
            setCurrentTime(0);
            break;
          
          case 'seek':
            setCurrentTime(message.time);
            break;
          
          case 'queue_update':
            console.log('[SessionContext] Queue update received:', message);
            console.log('[SessionContext] New queue length:', message.queue?.length);
            setSongQueue(message.queue || []);
            break;
          
          case 'reaction':
            setFireCount(message.fire_count || 0);
            // Update per-song fire count
            if (message.song_id && message.song_fire_count !== undefined) {
              setSongFireCounts(prev => ({ ...prev, [message.song_id]: message.song_fire_count }));
            }
            break;
          
          case 'user_joined':
            // CRITICAL: Preload new user's profile image for instant display
            const newUsers = message.users || [];
            newUsers.forEach(user => {
              if (user.profileImage) {
                const img = new Image();
                img.src = user.profileImage;
              }
            });
            setSessionUsers(newUsers);
            // Fetch missing profile images from API
            enrichUsersWithProfileImages(newUsers);
            break;
          
          case 'user_left':
            setSessionUsers(prev => prev.filter(u => u.id !== message.user?.id));
            break;
          
          case 'settings_update':
            // Update session settings including title
            console.log('[SessionContext] Settings update received:', message.settings);
            if (message.settings) {
              setActiveSessionData(prev => ({
                ...prev,
                ...message.settings
              }));
            }
            break;
          
          case 'session_ended':
            // Host ended the session - show modal and leave
            console.log('[SessionContext] Session ended by host:', message.message);
            
            // STOP MUSICKIT IMMEDIATELY
            if (stopMusicCallbackRef.current) {
              console.log('[SessionContext] Stopping MusicKit...');
              stopMusicCallbackRef.current();
            }
            
            // Only show modal for GUESTS (not the host who ended it)
            if (!isEndingSessionRef.current) {
              setSessionEndedMessage(message.message || 'The host has ended the session');
              setShowSessionEndedModal(true);
            }
            
            // Reset the flag
            isEndingSessionRef.current = false;
            
            // Clear session state
            setIsInActiveSession(false);
            setActiveSessionData(null);
            setSongQueue([]);
            setSessionUsers([]);
            setIsPlaying(false);
            setCurrentTrackIndex(0);
            setCurrentTime(0);
            localStorage.removeItem('tapInActiveSession');
            break;
          
          default:
            break;
        }
      } catch (e) {
        console.error('[SessionContext] Error parsing WS message:', e);
      }
    };

    ws.onclose = (event) => {
      console.log('[SessionContext] WebSocket closed:', event.code, 'for session:', currentSessionIdRef.current);
      isConnectingRef.current = false;
      
      // Debounce the disconnect state to prevent flickering during reconnects
      wsConnectedTimeoutRef.current = setTimeout(() => {
        setWsConnected(false);
      }, 500);
      
      // Only attempt reconnection on true connection failures (not intentional closes)
      // 1000 = normal close, 1005 = no status code, 1006 = abnormal close
      // Only reconnect on actual failures, not on intentional closes
      if (event.code !== 1000) {
        const storedSession = localStorage.getItem('tapInActiveSession');
        if (storedSession && currentSessionIdRef.current === sessionId) {
          // Clear any existing reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[SessionContext] Attempting reconnection to session:', sessionId);
            connectWebSocket(sessionId);
          }, 5000);
        }
      }
    };

    ws.onerror = (error) => {
      console.error('[SessionContext] WebSocket error:', error);
      isConnectingRef.current = false;
    };
  }, [user]); // Only depend on user - not activeSessionData

  // Reconnect to session on page load if we have a stored session
  const hasAttemptedReconnect = useRef(false);
  useEffect(() => {
    // Only attempt reconnect once on mount, and only if we're not already connected/connecting
    const isDisconnected = !wsRef.current || wsRef.current.readyState === WebSocket.CLOSED;
    const shouldReconnect = activeSessionData?.id && user && isDisconnected && !hasAttemptedReconnect.current && !isConnectingRef.current;
    
    if (shouldReconnect) {
      hasAttemptedReconnect.current = true;
      console.log('[SessionContext] Reconnecting to stored session on mount:', activeSessionData.id);
      connectWebSocket(activeSessionData.id);
    }
  }, [user?.id, activeSessionData?.id]); // Only depend on IDs, not objects or functions

  // CRITICAL: Reconnect WebSocket when user returns to the app (tab switch, phone unlock)
  // Mobile browsers kill WebSocket connections when the tab is backgrounded
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeSessionData?.id && user) {
        const isDisconnected = !wsRef.current || wsRef.current.readyState === WebSocket.CLOSED || wsRef.current.readyState === WebSocket.CLOSING;
        if (isDisconnected && !isConnectingRef.current) {
          console.log('[SessionContext] Tab became visible - reconnecting to session:', activeSessionData.id);
          connectWebSocket(activeSessionData.id);
        } else if (wsRef.current?.readyState === WebSocket.OPEN) {
          // Connection is alive - request a fresh sync to restore queue state
          console.log('[SessionContext] Tab visible - requesting sync');
          try {
            wsRef.current.send(JSON.stringify({ action: 'sync_request' }));
          } catch (e) {
            console.warn('[SessionContext] Failed to send sync_request, reconnecting');
            connectWebSocket(activeSessionData.id);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id, activeSessionData?.id]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    console.log('[SessionContext] Disconnecting WebSocket');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsConnectedTimeoutRef.current) {
      clearTimeout(wsConnectedTimeoutRef.current);
      wsConnectedTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User left session');
      wsRef.current = null;
    }
    isConnectingRef.current = false;
    currentSessionIdRef.current = null;
    hasReceivedInitialSyncRef.current = false;
    setWsConnected(false);
  }, []);

  // Send WebSocket message
  const sendWsMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Join a session and set it as active
  const enterSession = useCallback((session) => {
    // CRITICAL: Don't reset state if we're already in this session
    // This prevents music from stopping when navigating back from mini player
    if (activeSessionData?.id === session.id && wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[SessionContext] Already in this session, not resetting state');
      setCurrentSession(session);
      return;
    }
    
    // Also check if we're currently connecting to this session
    if (currentSessionIdRef.current === session.id && isConnectingRef.current) {
      console.log('[SessionContext] Already connecting to this session, skipping');
      setCurrentSession(session);
      return;
    }
    
    console.log('[SessionContext] Entering NEW session:', session.id);
    
    // CRITICAL: Clear ALL existing state from previous session - NEW SESSION = EMPTY
    setSongQueue([]);
    setSessionUsers([]);
    setIsPlaying(false);
    setCurrentTrackIndex(0);
    setCurrentTime(0);
    setFireCount(0);
    setSongFireCounts({});
    setFiredSongIds(new Set());
    
    setActiveSessionData(session);
    setCurrentSession(session);
    setIsInActiveSession(true);
    
    // Connect WebSocket - will receive fresh state via sync_state
    connectWebSocket(session.id);
  }, [connectWebSocket, activeSessionData?.id]);

  // Leave the session screen but stay in the session
  const minimizeSession = useCallback(() => {
    // Keep connection, just clear view
  }, []);

  // Fully leave/end the session, with option to save as playlist
  const exitSession = useCallback(async (endSession = false, saveAsPlaylist = false) => {
    console.log('[SessionContext] exitSession called - endSession:', endSession, 'saveAsPlaylist:', saveAsPlaylist);
    
    // If HOST is ending the session, set flag to skip showing them the modal
    if (endSession) {
      isEndingSessionRef.current = true;
    }
    
    if (activeSessionData && api) {
      try {
        // If ending and saving as playlist
        if (endSession && saveAsPlaylist && songQueue.length > 0) {
          try {
            await api.post('/api/playlists', {
              name: activeSessionData.title || 'Saved Session',
              tracks: songQueue.map(track => ({
                id: track.id,
                title: track.title,
                artist: track.artist,
                artwork_url: track.artwork_url
              }))
            });
            console.log('[SessionContext] Session saved as playlist');
          } catch (playlistError) {
            console.error('[SessionContext] Failed to save playlist:', playlistError);
          }
        }
        
        if (endSession) {
          await api.post(`/api/sessions/${activeSessionData.id}/end`);
        } else {
          await api.post(`/api/sessions/${activeSessionData.id}/leave`);
        }
      } catch (e) {
        // Continue with cleanup
        console.error('[SessionContext] API error during exit:', e);
        // Reset flag on error
        isEndingSessionRef.current = false;
      }
    }
    
    // CRITICAL: Disconnect WebSocket FIRST
    disconnectWebSocket();
    
    // CRITICAL: Stop music when leaving session (especially for guests)
    if (stopMusicCallbackRef.current) {
      console.log('[SessionContext] Stopping music on session exit');
      try {
        stopMusicCallbackRef.current();
      } catch (e) {
        console.error('[SessionContext] Error stopping music:', e);
      }
    }
    
    // CRITICAL: Clear ALL state - NO music should persist
    setActiveSessionData(null);
    setCurrentSession(null);
    setIsInActiveSession(false);
    setIsPlaying(false);
    setSongQueue([]);
    setSessionUsers([]);
    setCurrentTrackIndex(0);
    setCurrentTime(0);
    setFireCount(0);
    setSongFireCounts({});
    setFiredSongIds(new Set());
    setPendingAction(null);
    
    // CRITICAL: Clear localStorage session data
    try {
      localStorage.removeItem('tapInActiveSession');
    } catch (e) {
      // ignore
    }
    
    console.log('[SessionContext] Session fully exited - ALL state cleared');
  }, [activeSessionData, api, disconnectWebSocket, songQueue]);

  // Try to join a new session
  const tryJoinSession = useCallback((session) => {
    if (isInActiveSession && activeSessionData && activeSessionData.id === session.id) {
      setCurrentSession(session);
      return true;
    }
    
    if (isInActiveSession && activeSessionData) {
      setPendingAction({ type: 'join', data: session });
      setShowLeaveSessionModal(true);
      return false;
    }
    
    enterSession(session);
    return true;
  }, [isInActiveSession, activeSessionData, enterSession]);

  // Try to play a playlist
  const tryPlayPlaylist = useCallback((playlist) => {
    if (isInActiveSession && activeSessionData) {
      setPendingAction({ type: 'play', data: playlist });
      setShowLeaveSessionModal(true);
      return false;
    }
    return true;
  }, [isInActiveSession, activeSessionData]);

  // Handle leaving current session for pending action
  const handleLeaveAndContinue = useCallback(async () => {
    const action = pendingAction;
    await exitSession(false);
    
    if (action?.type === 'join') {
      enterSession(action.data);
    }
  }, [pendingAction, exitSession, enterSession]);

  // Get current track from queue
  const getCurrentTrack = useCallback(() => {
    return songQueue[currentTrackIndex] || songQueue[0] || null;
  }, [songQueue, currentTrackIndex]);

  // Get previous track
  const getPreviousTrack = useCallback(() => {
    if (!songQueue.length) return null;
    const prevIndex = currentTrackIndex > 0 ? currentTrackIndex - 1 : songQueue.length - 1;
    return songQueue[prevIndex];
  }, [songQueue, currentTrackIndex]);

  // Get next track
  const getNextTrack = useCallback(() => {
    if (!songQueue.length) return null;
    const nextIndex = (currentTrackIndex + 1) % songQueue.length;
    return songQueue[nextIndex];
  }, [songQueue, currentTrackIndex]);

  // Skip to next track - synced
  // Optimistically updates local index BEFORE the WS roundtrip so that
  // rapid clicks compute the correct next track instead of using stale state.
  const skipNext = useCallback(() => {
    setCurrentTrackIndex(prev => {
      const queue = songQueueRef.current || [];
      if (queue.length === 0) return prev;
      return (prev + 1) % queue.length;
    });
    sendWsMessage({ action: 'skip_next' });
  }, [sendWsMessage]);

  // Skip to previous track - synced
  const skipPrevious = useCallback(() => {
    setCurrentTrackIndex(prev => Math.max(0, prev - 1));
    sendWsMessage({ action: 'skip_prev' });
  }, [sendWsMessage]);

  // Play - synced with audio sync manager
  const play = useCallback((time, trackId = null, trackInfo = null) => {
    sendWsMessage({ 
      action: 'play', 
      current_time: time,
      track_id: trackId,
      track_info: trackInfo,
    });
  }, [sendWsMessage]);

  // Pause - synced
  const pause = useCallback((time) => {
    sendWsMessage({ action: 'pause', current_time: time });
  }, [sendWsMessage]);

  // Toggle play/pause - synced
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause(currentTime);
    } else {
      play(currentTime);
    }
  }, [isPlaying, currentTime, play, pause]);

  // Seek - synced
  const seekTo = useCallback((time) => {
    sendWsMessage({ action: 'seek', time });
  }, [sendWsMessage]);

  // Send fire reaction - synced (tracks per song to prevent double-firing)
  const sendFireReaction = useCallback((songId) => {
    if (songId && firedSongIds.has(songId)) {
      return; // Already fired for this song
    }
    if (songId) {
      setFiredSongIds(prev => new Set([...prev, songId]));
    }
    sendWsMessage({ action: 'reaction', type: 'fire', song_id: songId });
  }, [sendWsMessage, firedSongIds]);

  // Add track to queue (inserts after current) - synced with optimistic update
  const addToQueue = useCallback(async (sessionId, track) => {
    console.log('[SessionContext] addToQueue called:', { 
      sessionId, 
      trackTitle: track?.title,
      wsConnected,
      wsReadyState: wsRef.current?.readyState,
      WebSocketOPEN: WebSocket.OPEN
    });
    
    // Validate track has required properties
    if (!track || !track.id) {
      console.error('[SessionContext] Invalid track - missing id:', track);
      return null;
    }
    
    if (!wsRef.current) {
      console.error('[SessionContext] WebSocket ref is null, cannot add track');
      return null;
    }
    
    if (wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[SessionContext] WebSocket not in OPEN state:', wsRef.current.readyState);
      return null;
    }
    
    // CRITICAL: Optimistic update - add to local queue immediately for instant feedback
    // Add position/order and metadata to the track
    const trackWithAddedBy = { 
      ...track, 
      position: songQueue.length,
      added_at: new Date().toISOString(),
      added_by: { 
        id: user?.id, 
        username: user?.username || user?.display_name || 'Guest' 
      } 
    };
    
    // Update local queue immediately (optimistic update)
    setSongQueue(prev => {
      // Check if track already exists to prevent duplicates
      if (prev.some(t => t.id === track.id)) {
        console.log('[SessionContext] Track already in queue, skipping optimistic add');
        return prev;
      }
      console.log('[SessionContext] Optimistic add - track added to local queue at position:', prev.length);
      return [...prev, trackWithAddedBy];
    });
    
    const message = { 
      action: 'add_track', 
      track: trackWithAddedBy
    };
    
    console.log('[SessionContext] Sending WebSocket message:', message);
    sendWsMessage(message);
    return track;
  }, [sendWsMessage, user, setSongQueue]);

  // Remove from queue - synced
  const removeFromQueue = useCallback(async (sessionId, trackIndex) => {
    sendWsMessage({ action: 'remove_track', track_index: trackIndex });
  }, [sendWsMessage]);

  // Reorder queue - synced (host only)
  const reorderQueue = useCallback(async (fromIndex, toIndex) => {
    sendWsMessage({ action: 'reorder_track', from_index: fromIndex, to_index: toIndex });
  }, [sendWsMessage]);
  
  // Register callback for AUDIO_SYNC messages
  // This allows SessionView to handle sync with MusicKit
  const registerAudioSyncCallback = useCallback((callback) => {
    audioSyncCallbackRef.current = callback;
    
    // Process any queued messages that arrived before callback was ready
    if (pendingAudioSyncMessagesRef.current.length > 0) {
      console.log('[SessionContext] Processing', pendingAudioSyncMessagesRef.current.length, 'queued AUDIO_SYNC messages');
      const queuedMessages = [...pendingAudioSyncMessagesRef.current];
      pendingAudioSyncMessagesRef.current = [];
      
      // Process ONCE with a single delay — no retries that cause triple-loading
      // Multiple retries interrupt in-progress MusicKit loads on slow mobile devices
      setTimeout(() => {
        queuedMessages.forEach(msg => {
          console.log('[SessionContext] Processing queued message:', msg.action);
          if (audioSyncCallbackRef.current) {
            audioSyncCallbackRef.current(msg);
          }
        });
      }, 300);
    }
    
    return () => {
      audioSyncCallbackRef.current = null;
    };
  }, []);

  // Register callback to stop music when session ends
  const registerStopMusicCallback = useCallback((callback) => {
    stopMusicCallbackRef.current = callback;
    return () => {
      stopMusicCallbackRef.current = null;
    };
  }, []);

  // API methods
  const createSession = useCallback(async (title, isPublic = true, maxGuests = 10) => {
    if (!api) return null;
    setLoading(true);
    try {
      // Clear any existing session data before creating new one
      localStorage.removeItem('tapInActiveSession');
      
      // IMPORTANT: Clear the song queue for new sessions
      // New sessions should always start with empty queue
      setSongQueue([]);
      setCurrentTrackIndex(0);
      setCurrentTime(0);
      setIsPlaying(false);
      
      const response = await api.post('/api/sessions/create', {
        title,
        is_public: isPublic,
        max_guests: maxGuests,
      });
      const session = response.data;
      if (session && session.id) {
        enterSession(session);
      }
      return session;
    } catch (error) {
      console.error('[SessionContext] createSession error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [api, enterSession]);

  const fetchMySessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/sessions/my-sessions');
      setMySessions(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchActiveSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/sessions/active');
      setActiveSessions(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchSession = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/sessions/${sessionId}`);
      setCurrentSession(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const joinSession = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const response = await api.post(`/api/sessions/${sessionId}/join`);
      enterSession(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  }, [api, enterSession]);

  const leaveSession = useCallback(async () => {
    await exitSession(false, false);
  }, [exitSession]);

  const endSession = useCallback(async (saveAsPlaylist = false) => {
    await exitSession(true, saveAsPlaylist);
  }, [exitSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, [disconnectWebSocket]);

  const value = {
    // Current view state
    currentSession,
    setCurrentSession,
    
    // Persistent session state
    activeSessionData,
    isInActiveSession,
    setIsInActiveSession,
    isPlaying,
    currentTime,
    
    // WebSocket state
    wsConnected,
    sessionUsers,
    fireCount,
    songFireCounts,
    
    // Song queue
    songQueue,
    setSongQueue,
    currentTrackIndex,
    getCurrentTrack,
    getPreviousTrack,
    getNextTrack,
    
    // Session actions
    enterSession,
    minimizeSession,
    exitSession,
    tryJoinSession,
    tryPlayPlaylist,
    
    // Synced playback controls
    play,
    pause,
    togglePlayPause,
    skipNext,
    skipPrevious,
    seekTo,
    
    // Synced reactions
    sendFireReaction,
    
    // Audio sync callback registration
    registerAudioSyncCallback,
    
    // Stop music callback registration (for when session ends)
    registerStopMusicCallback,
    
    // Modal state
    showLeaveSessionModal,
    setShowLeaveSessionModal,
    pendingAction,
    handleLeaveAndContinue,
    
    // Session ended by host modal
    showSessionEndedModal,
    setShowSessionEndedModal,
    sessionEndedMessage,
    dismissSessionEndedModal: () => {
      setShowSessionEndedModal(false);
      setSessionEndedMessage('');
      // IMPORTANT: Clear all session state so guest returns to clean home screen
      setCurrentSession(null);
      setActiveSessionData(null);
      setSongQueue([]);
      setCurrentTrackIndex(0);
      setCurrentTime(0);
      setIsPlaying(false);
    },
    
    // API methods
    mySessions,
    activeSessions,
    loading,
    createSession,
    fetchMySessions,
    fetchActiveSessions,
    fetchSession,
    joinSession,
    leaveSession,
    endSession,
    addToQueue,
    removeFromQueue,
    reorderQueue,
    
    // Track which songs user has fired
    firedSongIds,
    
    // WebSocket message sending (for settings updates, etc.)
    sendWsMessage,
    
    // Update session data directly (for settings changes)
    updateSessionData: (updates) => setActiveSessionData(prev => prev ? { ...prev, ...updates } : prev),
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
      {/* Session Ended Modal - Shown when host ends session */}
      {showSessionEndedModal && (
        <SessionEndedModal 
          message={sessionEndedMessage} 
          onDismiss={() => {
            // Clear modal
            setShowSessionEndedModal(false);
            setSessionEndedMessage('');
            // IMPORTANT: Clear ALL session state so guest is fully out of session
            setCurrentSession(null);
            setActiveSessionData(null);
            setSongQueue([]);
            setCurrentTrackIndex(0);
            setCurrentTime(0);
            setIsPlaying(false);
            // Clear localStorage session data
            localStorage.removeItem('tapInActiveSession');
          }} 
        />
      )}
    </SessionContext.Provider>
  );
};

export default SessionContext;
